import { AVAILABLE_DAYS, ISSUES } from "@/data/issues";
import { fetchDay } from "./api";

export interface SearchEntry {
  day: number;
  index: number;
  word: string;
  ipa: string;
  pos: string;
  meaning_vi: string;
  theme_en: string;
  theme_vi: string;
}

let indexPromise: Promise<readonly SearchEntry[]> | null = null;

/**
 * Build a flat search index across every available day. Lazy: built on the
 * first call (typically the first time the user opens cmd-K) and cached for
 * the rest of the session. Uses the same in-memory cache as `useDayData`,
 * so opening Day 1 first warms part of the index for free.
 */
export function getSearchIndex(): Promise<readonly SearchEntry[]> {
  if (indexPromise) return indexPromise;

  indexPromise = (async () => {
    const days = ISSUES.filter((i) => AVAILABLE_DAYS.has(i.day));
    const settled = await Promise.allSettled(
      days.map((issue) =>
        fetchDay(issue.day).then((data) =>
          data.words.map<SearchEntry>((w) => ({
            day: issue.day,
            index: w.index,
            word: w.word,
            ipa: w.ipa,
            pos: w.pos,
            meaning_vi: w.meaning_vi,
            theme_en: issue.theme_en,
            theme_vi: issue.theme_vi,
          }))
        )
      )
    );
    const all: SearchEntry[] = [];
    for (const r of settled) {
      if (r.status === "fulfilled") all.push(...r.value);
    }
    return all;
  })();

  indexPromise.catch(() => {
    indexPromise = null;
  });
  return indexPromise;
}

/**
 * Substring filter on word / meaning_vi / theme. Case-insensitive.
 * Returns at most `limit` results.
 */
export function searchEntries(
  entries: readonly SearchEntry[],
  query: string,
  limit = 20
): SearchEntry[] {
  const q = query.trim().toLowerCase();
  if (!q) return entries.slice(0, Math.min(limit, 8));
  const out: SearchEntry[] = [];
  for (const e of entries) {
    if (
      e.word.toLowerCase().includes(q) ||
      e.meaning_vi.toLowerCase().includes(q) ||
      e.theme_en.toLowerCase().includes(q) ||
      e.theme_vi.toLowerCase().includes(q)
    ) {
      out.push(e);
      if (out.length >= limit) break;
    }
  }
  return out;
}
